import Vue from "vue";
import VueRouter from "vue-router";
import store from "@/store";
import { ROUTE_NAMES, ROUTES } from "@/constants/routeConstants";
import { PRIVILEGES } from "@/constants/privileges";
import BaseLayout from "@/layout/BaseLayout";
import api from "@/services/api";
import { ENDPOINTS } from "@/constants/endpoints";

Vue.use(VueRouter);

const ifAuthenticated = async () => {
  if (store.getters["auth/isAuthenticated"]) return true;

  try {
    await store.dispatch("auth/initSession");
  } catch (err) {
    console.error(err);
  }

  return !!store.getters["auth/isAuthenticated"];
};

const hasPrivilege = (privilege, roleType) => async (to, from, next) => {
  const authenticated = await ifAuthenticated();
  if (authenticated === true) {
    try {
      await api.get(ENDPOINTS.auth_session);
    } catch (err) {
      let w = window;
      window.location.href = w.location.href;
      return next(ROUTES.login);
    }
  }

  if (authenticated === false) {
    return next(ROUTES.login);
  }
  if (roleType) {
    if (
      (await store.dispatch("auth/hasPrivilege", privilege)) &&
      (await store.dispatch("auth/hasCorrectRoleType", roleType))
    ) {
      return next();
    }
  }
  if (await store.dispatch("auth/hasPrivilege", privilege)) {
    return next();
  }

  return next(ROUTES.dashboard);
};

const hasPageQuery = (route) => {
  return !!route.query.page;
};

const returnRoute = (to, from = null) => {
  // get coop query from "from"
  if (from) {
    return Object.assign({}, to, {
      query: {},
    });
  }
  const query = { ...to.query };
  return Object.assign({}, to, { query });
};

let firstLoad = true;

const routes = [
  {
    path: "/landing/terms",
    name: ROUTE_NAMES.terms,
    component: () => import("@/views/Haldusnet2/terms.vue"),
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/landing/terms2",
    name: ROUTE_NAMES.terms2,
    component: () => import("@/views/Haldusnet2/terms.vue"),
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/:locale/landing",
    name: ROUTE_NAMES.landing,
    component: () => import("@/views/Haldusnet2/Landing.vue"),
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/:locale?",
    redirect: { name: ROUTE_NAMES.landing, params: { locale: "et" } },
  },
  {
    path: "/:locale",
    name: "locale",
    component: BaseLayout,
    children: [
      {
        path: ROUTES.resetPassword,
        name: ROUTE_NAMES.resetPassword,
        component: () => import("@/components/auth/Login.vue"),
        meta: {
          requiresAuth: false,
        },
      },
      {
        path: ROUTES.dashboard,
        name: ROUTE_NAMES.dashboard,
        // commented out for roles that don't have privilege
        // beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.view),
        component: () => import("@/views/Haldusnet2/Dashboard"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: ROUTES.myApartment,
        name: ROUTE_NAMES.myApartment,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.view),
        component: () => import("@/views/Haldusnet2/Apartment"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: ROUTES.contacts,
        name: ROUTE_NAMES.contacts,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.view),
        component: () => import("@/views/Haldusnet2/Contacts"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: ROUTES.county,
        name: ROUTE_NAMES.county,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.county),
        component: () => import("@/views/Haldusnet2/County"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: ROUTES.onBoarding,
        name: ROUTE_NAMES.onBoarding,
        component: () => import("@/views/Haldusnet2/OnBoarding.vue"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: ROUTES.rejected,
        name: ROUTE_NAMES.rejected,
        component: () => import("@/views/Haldusnet2/termsRejected.vue"),
      },
      {
        path: ROUTES.readings,
        name: ROUTE_NAMES.readings,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.measurers),
        component: () => import("@/views/Haldusnet2/Readings"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: ROUTES.entries,
        name: ROUTE_NAMES.entries,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.entries),
        component: () => import("@/views/Haldusnet2/Readings/Entries.vue"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: ROUTES.gaugeEdit,
        name: ROUTE_NAMES.gaugeEdit,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.measurerEdit),
        component: () => import("@/views/Haldusnet2/Gauge/MeasurerEditAll.vue"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: ROUTES.gauge,
        name: ROUTE_NAMES.gauge,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.gauge),
        component: () => import("@/views/Haldusnet2/Gauge/GaugeList"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: ROUTES.periods,
        name: ROUTE_NAMES.periods,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.periods),
        component: () => import("@/views/Haldusnet2/Periods/Periods.vue"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: ROUTES.measureAddApartment,
        name: ROUTE_NAMES.measureAddApartment,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.measurerEdit),
        component: () => import("@/views/Haldusnet2/Gauge/MeasurerAdd"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: ROUTES.measureAddCoop,
        name: ROUTE_NAMES.measureAddCoop,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.measurerEdit),
        component: () => import("@/views/Haldusnet2/Gauge/MeasurerAdd"),
        meta: {
          requiresAuth: true,
        },
      },

      {
        path: ROUTES.readingsImport,
        name: ROUTE_NAMES.readingsImport,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.boardMeasurers),
        component: () => import("@/views/Haldusnet2/readingsImport"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: ROUTES.invoices,
        name: ROUTE_NAMES.invoices,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.boardInvoice),
        component: () => import("@/views/Haldusnet2/Invoices/Invoices.vue"),
        meta: {
          requiresAuth: true,
          displayName: "invoices",
        },
      },
      {
        path: ROUTES.notifications,
        name: ROUTE_NAMES.notifications,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.notification),
        component: () => import("@/views/Haldusnet2/Notifications"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: ROUTES.profile,
        name: ROUTE_NAMES.profile,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.user),
        component: () => import("@/views/Haldusnet2/profile"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: ROUTES.oneNotification,
        name: ROUTE_NAMES.oneNotification,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.notification),
        component: () => import("@/views/Haldusnet2/SingleNotification"),
        meta: {
          requiresAuth: true,
          parent: "Notifications",
        },
      },
      {
        path: ROUTES.emailNotificationDetail,
        name: ROUTE_NAMES.emailNotificationDetail,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.kvatroNotification),
        component: () => import("@/views/Haldusnet2/SingleNotification"),
        meta: {
          requiresAuth: true,
          parent: "reminder_2",
        },
      },
      {
        path: ROUTES.forum,
        name: ROUTE_NAMES.forum,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.forumView),
        component: () => import("@/views/Haldusnet2/Forum"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: ROUTES.forumPost,
        name: ROUTE_NAMES.forumPost,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.forumView),
        component: () => import("@/views/Haldusnet2/ForumPost"),
        meta: {
          requiresAuth: true,
          parent: "forum_page",
        },
      },
      {
        path: ROUTES.bills,
        name: ROUTE_NAMES.bills,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.view),
        component: () => import("@/views/Haldusnet2/Invoice"),
        meta: {
          requiresAuth: true,
          displayName: "invoices",
        },
      },
      {
        path: ROUTES.documents,
        name: ROUTE_NAMES.documents,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.document),
        component: () => import("@/views/Haldusnet2/Documents/DocumentList"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: ROUTES.document,
        name: ROUTE_NAMES.document,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.view),
        component: () => import("@/views/Haldusnet2/Documents/Document"),
        meta: {
          requiresAuth: true,
          parent: "Documents",
        },
      },
      {
        path: ROUTES.task,
        name: ROUTE_NAMES.task,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.taskOrderingView),
        component: () => import("@/views/Haldusnet2/Tasks.vue"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: ROUTES.maintenance,
        name: ROUTE_NAMES.maintenance,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.taskMaintenanceView),
        component: () => import("@/views/Haldusnet2/Maintenance"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: ROUTES.statistics,
        name: ROUTE_NAMES.statistics,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.view),
        component: () => import("@/views/Haldusnet2/Statistics"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: ROUTES.maintenanceTask,
        name: ROUTE_NAMES.maintenanceTask,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.maintenance),
        component: () => import("@/views/Haldusnet2/MaintenanceTask"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: ROUTES.taskDetail,
        name: ROUTE_NAMES.taskDetail,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.maintenance),
        component: () => import("@/views/Haldusnet2/Task.vue"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: ROUTES.meetings,
        name: ROUTE_NAMES.meetings,
        beforeEnter: hasPrivilege(PRIVILEGES.meeting.view),
        component: () =>
          import("@/views/koosolekud/Meeting/MeetingDashboard.vue"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: ROUTES.new_meeting,
        name: ROUTE_NAMES.new_meeting,
        beforeEnter: hasPrivilege(PRIVILEGES.meeting.addEdit),
        component: () => import("@/views/koosolekud/Meeting/CreateMeeting.vue"),
        meta: {
          requiresAuth: true,
          parent: "meetingDashboard",
        },
      },
      {
        path: ROUTES.meeting,
        name: ROUTE_NAMES.meeting,
        beforeEnter: hasPrivilege(PRIVILEGES.meeting.addEdit),
        component: () => import("@/views/koosolekud/Meeting/CreateMeeting.vue"),
        meta: {
          requiresAuth: true,
          parent: "meetingDashboard",
        },
      },
      {
        path: ROUTES.voting,
        name: ROUTE_NAMES.voting,
        beforeEnter: hasPrivilege(PRIVILEGES.meeting.view),
        component: () => import("@/views/koosolekud/Voting/Voting.vue"),
        meta: {
          requiresAuth: true,
          parent: "meetingDashboard",
        },
      },
      {
        path: ROUTES.paper_voting,
        name: ROUTE_NAMES.paper_voting,
        beforeEnter: hasPrivilege(PRIVILEGES.meeting.addEdit),
        component: () => import("@/views/koosolekud/Voting/PaperVoting.vue"),
        meta: {
          requiresAuth: true,
          parent: "meetingDashboard",
        },
      },
      {
        path: ROUTES.results,
        name: ROUTE_NAMES.results,
        beforeEnter: hasPrivilege(PRIVILEGES.meeting.view),
        component: () => import("@/views/koosolekud/Results/Results"),
        meta: {
          requiresAuth: true,
          parent: "meetingDashboard",
        },
      },

      {
        path: ROUTES.participation_list,
        name: ROUTE_NAMES.participation_list,
        beforeEnter: hasPrivilege(PRIVILEGES.meeting.addEdit),
        component: () => import("@/views/koosolekud/Results/ParticipationList"),
        meta: {
          requiresAuth: true,
          parent: "meetingDashboard",
        },
      },
      {
        path: ROUTES.cooperative,
        name: ROUTE_NAMES.cooperative,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.viewApartment, [
          "kvatro",
        ]),
        component: () =>
          import("@/views/Haldusnet2/Cooperatives/CooperativeDetail.vue"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: ROUTES.cooperativeUser,
        name: ROUTE_NAMES.cooperativeUser,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.cooperativePersonsView, [
          "kvatro",
        ]),
        component: () => import("@/views/Haldusnet2/Users/UserDetail.vue"),
        meta: {
          requiresAuth: true,
          parent: "cooperative",
        },
      },
      {
        path: ROUTES.apartmentUser,
        name: ROUTE_NAMES.apartmentUser,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.boardResidents, [
          "kvatro",
        ]),
        component: () => import("@/views/Haldusnet2/Users/UserDetail.vue"),
        meta: {
          requiresAuth: true,
          parent: "cooperative",
        },
      },
      {
        path: ROUTES.apartment,
        name: ROUTE_NAMES.apartment,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.viewApartment, [
          "kvatro",
        ]),
        component: () =>
          import(
            "@/views/Haldusnet2/Cooperatives/Apartment/ApartmentDetail.vue"
          ),
        meta: {
          requiresAuth: true,
          parent: "cooperative",
        },
      },
      // Admin modules
      {
        path: ROUTES.documentCatalogues,
        name: ROUTE_NAMES.documentCatalogues,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.catalogues),
        component: () =>
          import(
            "@/views/Haldusnet2/DocumentCatalogues/DocumentCatalogues.vue"
          ),
        meta: {
          requiresAuth: true,
          adminModule: true,
        },
      },
      {
        path: ROUTES.roles,
        name: ROUTE_NAMES.roles,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.roles),
        component: () => import("@/views/Haldusnet2/Roles/Roles.vue"),
        meta: {
          requiresAuth: true,
          adminModule: true,
        },
      },
      {
        path: ROUTES.cooperatives,
        name: ROUTE_NAMES.cooperatives,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.viewCooperative),
        component: () =>
          import("@/views/Haldusnet2/Cooperatives/Cooperatives.vue"),
        meta: {
          requiresAuth: true,
          adminModule: true,
        },
      },
      {
        path: ROUTES.emailNotification,
        name: ROUTE_NAMES.emailNotification,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.kvatroNotification, [
          "kvatro",
        ]),
        component: () =>
          import("@/views/Haldusnet2/Notifications/EmailNotificaions.vue"),
        meta: {
          requiresAuth: true,
          adminModule: true,
        },
      },
      {
        path: ROUTES.cooperativeDetail,
        name: ROUTE_NAMES.cooperativeDetail,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.view, [
          "kvatro",
          "board",
        ]),
        component: () =>
          import("@/views/Haldusnet2/Cooperatives/CooperativeDetail.vue"),
        meta: {
          requiresAuth: true,
          adminModule: true,
          parent: "cooperatives",
        },
      },
      {
        path: ROUTES.apartmentDetail,
        name: ROUTE_NAMES.apartmentDetail,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.viewApartment),
        component: () =>
          import(
            "@/views/Haldusnet2/Cooperatives/Apartment/ApartmentDetail.vue"
          ),
        meta: {
          requiresAuth: true,
          adminModule: true,
          parent: "cooperatives",
        },
      },
      {
        path: ROUTES.cooperativeApartmentAll,
        name: ROUTE_NAMES.cooperativeApartmentAll,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.editApartment),
        component: () =>
          import(
            "@/views/Haldusnet2/Cooperatives/Apartment/ApartmentEditAll.vue"
          ),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: ROUTES.cooperativeUserDetail,
        name: ROUTE_NAMES.cooperativeUserDetail,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.cooperativePersonsView, [
          "kvatro",
        ]),
        component: () => import("@/views/Haldusnet2/Users/UserDetail.vue"),
        meta: {
          requiresAuth: true,
          adminModule: true,
          parent: "cooperatives",
        },
      },
      {
        path: ROUTES.cooperativePersonsAll,
        name: ROUTE_NAMES.cooperativePersonsAll,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.cooperativeResidentEdit),
        component: () =>
          import("@/views/Haldusnet2/Cooperatives/PersonsEditAll.vue"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: ROUTES.apartmentUserDetail,
        name: ROUTE_NAMES.apartmentUserDetail,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.boardResidents, [
          "kvatro",
        ]),
        component: () => import("@/views/Haldusnet2/Users/UserDetail.vue"),
        meta: {
          requiresAuth: true,
          adminModule: true,
          parent: "cooperatives",
        },
      },
      {
        path: ROUTES.users,
        name: ROUTE_NAMES.users,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.users),
        component: () => import("@/views/Haldusnet2/Users/Users.vue"),
        meta: {
          requiresAuth: true,
          adminModule: true,
        },
      },
      {
        path: ROUTES.userDetail,
        name: ROUTE_NAMES.userDetail,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.users, ["kvatro"]),
        component: () => import("@/views/Haldusnet2/Users/UserDetail.vue"),
        meta: {
          requiresAuth: true,
          adminModule: true,
          parent: "users",
        },
      },
      {
        path: ROUTES.taskAcceptance,
        name: ROUTE_NAMES.taskAcceptance,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.taskWork, [
          "kvatro",
          "board",
        ]),
        component: () => import("@/views/Haldusnet2/Task/TaskAcceptance.vue"),
        meta: {
          requiresAuth: true,
          adminModule: true,
        },
      },
      {
        path: ROUTES.taskAcceptanceDetail,
        name: ROUTE_NAMES.taskAcceptanceDetail,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.taskWork, ["kvatro"]),
        component: () => import("@/views/Haldusnet2/Task.vue"),
        meta: {
          requiresAuth: true,
          adminModule: true,
          parent: "taskAcceptance",
        },
      },
      {
        path: ROUTES.labels,
        name: ROUTE_NAMES.labels,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.labels),
        component: () => import("@/views/Haldusnet2/Label/Labels.vue"),
        meta: {
          requiresAuth: true,
          adminModule: true,
        },
      },
      {
        path: ROUTES.search,
        name: ROUTE_NAMES.search,
        beforeEnter: hasPrivilege(PRIVILEGES.haldusnet.view, ["kvatro"]),
        component: () => import("@/views/Haldusnet2/Search/Search.vue"),
        meta: {
          requiresAuth: true,
          adminModule: true,
          hasQueryParamPage: true,
          displayName: "search_result",
        },
      },
      // Other views
      {
        path: ROUTES.forbidden,
        name: ROUTE_NAMES.forbidden,
        component: () => import("@/views/Errors/PrivilegeError.vue"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: ROUTES.login,
        name: ROUTE_NAMES.login,
        component: () => import("@/components/auth/Login.vue"),
        meta: {
          requiresAuth: false,
        },
      },
      {
        path: ROUTES.activate,
        name: ROUTE_NAMES.activate,
        component: () => import("@/components/auth/Login.vue"),
        meta: {
          requiresAuth: false,
        },
      },
      {
        path: ROUTES.terms,
        name: ROUTE_NAMES.terms,
        component: () => import("@/views/Haldusnet2/terms.vue"),
      },
      {
        path: ROUTES.privacy,
        name: ROUTE_NAMES.privacy,
        component: () => import("@/views/Haldusnet2/privacy.vue"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: ROUTES.page_not_found,
        name: ROUTE_NAMES.page_not_found,
        component: () => import("@/views/Errors/PageNotFound.vue"),
        meta: {
          requiresAuth: false,
        },
      },
    ],
  },
];

const router = new VueRouter({
  mode: "history",
  routes,
});

router.link = function (url, paramsIn) {
  let params = {
    ...this.$route.params,
    ...(paramsIn !== undefined ? paramsIn : {}),
  };
  url = ":locale/" + url;
  for (let key in params) {
    let myRegExp = new RegExp(":" + key, "gi");
    url = url.replace(myRegExp, params[key]);
  }
  return "/" + url;
};

router.beforeEach(async (to, from, next) => {
  if (
    !store.getters["auth/session"] &&
    !store.getters["auth/sessionRequestDone"]
  ) {
    await store.dispatch("auth/checkSession");
    if (store.getters["auth/session"]) {
      if (to.name === ROUTE_NAMES.landing) {
        next({ name: ROUTE_NAMES.dashboard });
      } else {
        if (!to.meta.adminModule) {
          await ifAuthenticated();
          const route = returnRoute(to);
          next(route);
        } else {
          next();
        }
      }
    } else {
      if (to.matched.some((record) => record.meta.requiresAuth)) {
        next({ name: ROUTE_NAMES.login, params: { locale: "et" } });
      } else if (to.path.includes("/communication/forum/topic")) {
        next({ name: ROUTE_NAMES.login, params: { locale: "et" } });
      } else {
        next();
      }
    }
  } else if (
    to.path.includes("/otsing") &&
    to.fullPath.indexOf("keyword") === -1
  ) {
    let updatedNext = null;

    updatedNext = {
      ...to,
      query: {
        // This line is used to retain other query if there is any
        ...to.query,
        keyword: from.query.keyword !== undefined ? from.query.keyword : "",
      },
    };

    if (updatedNext) {
      console.log(updatedNext);
      next(updatedNext);
      return;
    }
  } else if (to.path.includes("/communication/forum/topic")) {
    if (!store.getters["user/isProfileLoaded"]) {
      next({ name: ROUTE_NAMES.login, params: { locale: "et" } });
    } else {
      const ids = to.path.split("/");
      next({
        name: ROUTE_NAMES.forumPost,
        params: { locale: "et", id: ids[2], topicId: ids[6] },
      });
    }
  } else if (to.matched.some((record) => record.meta.requiresAuth)) {
    // this route requires auth, check if logged in
    // if not, redirect to login page.
    await ifAuthenticated();
    if (!store.getters["user/isProfileLoaded"]) {
      next({ name: ROUTE_NAMES.login, params: { locale: "et" } });
    } else {
      // on first load get coop query from cookies
      // then get coop query from "from"
      if (!hasPageQuery(from) && firstLoad) {
        firstLoad = false;
        const route = returnRoute(to);
        next(route);
      } else {
        next(); // go to wherever I'm going
      }
    }
  } else {
    if (
      to.name === ROUTE_NAMES.login &&
      store.getters["auth/status"] !== null
    ) {
      if (await ifAuthenticated()) {
        next({ name: ROUTE_NAMES.dashboard, params: { locale: "et" } });
      }
    }
    next(); // does not require auth, make sure to always call next()!
  }
});

export default router;
